/**
 * Reading the batch while the server cannot be reached (PROJECT_SPEC.md §8.6; URS-8.6-001). When a GET for the
 * execution screen gets no answer, api.ts asks this module for the same answer in the same shape, built from
 * the device's replica. It never makes up what the replica does not hold: a field the replica cannot know is
 * left empty, an entry id is marked as local, and a batch that is not in the replica yields nothing (the
 * caller then fails as it always did).
 *
 * What it returns is the batch as the server last described it (`asOf`), not as it is now. The always-on
 * offline banner says so, and nothing read here is ever treated as the record: only the server's replay is.
 */
import type { WorkingSet } from "@corpex-ebr/domain";

const BATCH_PATH = /^\/api\/v1\/batches\/([^/?]+)(?:\/(steps|recipe|values))?(?:\?.*)?$/;
/** A replica entry has no server event; this prefix makes that visible instead of inventing an id. */
export const LOCAL_ID_PREFIX = "replica:";

export function replicaAnswer(ws: WorkingSet | null, path: string): unknown {
  const m = BATCH_PATH.exec(path);
  const b = m?.[1] ? ws?.batches[m[1]] : undefined;
  if (!ws || !b) return undefined;
  switch (m?.[2]) {
    case undefined:
      return {
        id: b.batchId,
        batch_number: b.batchNumber,
        status: b.status,
        planned_qty: b.content.header.batchSize.quantity,
        mbr_snapshot_id: null,
        hold_reason: null,
        created_at_server: ws.asOf,
        started_at_server: null,
      };
    case "recipe":
      return { content: b.content, snapshot: { content_hash: b.contentHash, as_of: ws.asOf } };
    case "steps":
      return b.steps.map((s) => ({
        batch_id: b.batchId,
        step_execution_id: `${LOCAL_ID_PREFIX}${s.stepId}:${s.instanceOrdinal}`,
        mbr_step_id: s.stepId,
        instance_ordinal: s.instanceOrdinal,
        state: s.state,
        started_at_server: null,
        completed_at_server: null,
        current_values: String(b.values.filter((v) => v.stepId === s.stepId).length),
        out_of_limit_values: "0",
        corrections: "0",
      }));
    default:
      return b.values.map((v) => ({
        id: `${LOCAL_ID_PREFIX}${v.stepId}:${v.parameterCode}`,
        mbr_step_id: v.stepId,
        mbr_parameter_id: v.parameterCode,
        value_numeric: v.numeric ? v.value : null,
        value_text: v.numeric ? null : v.value,
        value_bool: null,
        spec_evaluation: "N_A",
        entry_method: "KEYED",
        source_ref: null,
        is_current: true,
        supersedes_data_point_id: null,
        correction_reason_code: null,
        correction_reason_text: null,
        server_timestamp: ws.asOf,
        entered_by: "",
        on_behalf_of: null,
        event_id: "",
      }));
  }
}
